import { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Typography,
} from '@mui/material'
import { useDataProvider, useNotify, useGetMany } from 'react-admin'
import { useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import React from 'react'
import { isEmpty, get } from 'lodash-es'
import {
    Capitalize,
    getConnectorErrorMessage,
    useDatevClients,
    isDatevConnector,
} from '@thefront/pandipackV2'
// TODO this is custom for kombo..
import { showKomboConnect } from '@kombo-api/connect'
import { ContentBlock } from '../common'
import { POPUP_WINDOW_PARAMS, useThemeConfig } from '../../themeConfig'
import ConnectDialog from './ConnectDialog'

// format a string with placeholders like {{ key }}
const formatString = (string, context) => {
    return string.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
        const value = get(context, key, '')
        return ['string', 'number'].includes(typeof value) ? value : ''
    })
}

export const ExternalConnectorForm = ({
    extraTenantInfo,
    tenant,
    webhookUrl,
    connector,
}) => {
    const notify = useNotify()
    const dataProvider = useDataProvider()
    const theme = useThemeConfig()
    const [oAuth2Link, setOAuth2Link] = useState('')
    const [modalError, setModalError] = useState('')
    const [connected, setConnected] = useState(
        isConnected(tenant, extraTenantInfo.connector_name)
    )
    // Track if we're auto-connecting to avoid flashing the form
    const [isAutoConnecting, setIsAutoConnecting] = useState(null)
    const { authStatus, datevClients, datevClientErrors } = useDatevClients({
        connector,
        tenant,
        onLoaded: () => setIsAutoConnecting(false),
        onError: (err) => {
            setModalError(getConnectorErrorMessage(err))
            setIsAutoConnecting(false)
        },
    })

    // Compute configurable dialog title
    const templateContext = {
        integration_name: tenant.integration.name,
        integration_long_name: tenant.integration.long_name,
        integration_partner_name: tenant.integration.partner?.name,
        integration_documentation_url:
            tenant.integration.marketplace_settings?.docURL,
        connector_label: connector.label ?? connector.name,
    }
    const overrideTitle =
        tenant.integration.marketplace_settings?.authDialogTitle?.[
            connector.name
        ] || theme.configs.marketplaceSettings?.authDialogTitle
    const authDialogTitle = overrideTitle
        ? formatString(overrideTitle, templateContext)
        : `${connector.label || Capitalize(connector.name)} Authentication`

    // Compute configurable success message
    const overrideSuccessMessage =
        tenant.integration.marketplace_settings?.successMessage?.[
            connector.name
        ] || theme.configs.marketplaceSettings?.successMessage
    const successMessage = overrideSuccessMessage
        ? formatString(overrideSuccessMessage, templateContext)
        : 'Account connected successfully!'

    const postMessage = (message) => {
        const dest = window.opener ?? window.parent
        dest.postMessage(
            {
                message: message,
                webhook_url: webhookUrl,
                tenant: {
                    ...tenant,
                    integration: {
                        id: tenant.integration.id,
                        name: tenant.integration.name,
                    },
                },
            },
            '*'
        )
    }

    const connectBasic = (connectorName, values) => {
        setModalError('')
        dataProvider
            .V1_ACTION('tenants', {
                id: tenant.id,
                action: `connectors/${connectorName}/connect_basic`,
                method: 'PATCH',
                data: { secrets: values },
            })
            .then((res) => {
                console.log('Connect success')
            })
            .catch((err) => {
                console.debug(err)
                setModalError(getConnectorErrorMessage(err))
                postMessage('pandium-error')
            })
    }

    const disconnectRequest = () => {
        dataProvider
            .V1_ACTION('tenants', {
                id: tenant.id,
                action: `connectors/${connector.name}/disconnect`,
                method: 'PATCH',
            })
            .then(() => {
                console.debug('Disconnect success: ', connector.name)
                notify('Disconnect success!')
                postMessage('pandium-disconnect')
                setOAuth2Link('')
            })
    }

    const connectRequest = (
        connectorName,
        form_data = undefined,
        extra = {}
    ) => {
        setModalError('')
        setOAuth2Link('')
        dataProvider
            .V1_ACTION('tenants', {
                id: tenant.id,
                action: `connectors/${connectorName}/connect`,
                method: 'PATCH',
                data: {
                    form_data: form_data,
                    // if the EMP is in a popup, send the current url to be used as the redirect after /callback/oauth2
                    success_url:
                        window.parent !== window ? null : window.location.href,
                    ...extra,
                },
            })
            .then(async (res) => {
                if (connectorName === 'kombo') {
                    setKomboToken(await showKomboConnect(res.data.link))
                } else if (res.data.link || res.data.installUrl) {
                    // custom linnworks login flow uses installUrl, rest use link
                    const link = res.data.link || res.data.installUrl
                    console.debug('Opening new tab... ', link)
                    if (window.parent !== window) {
                        const popup = window.open(
                            link,
                            null,
                            POPUP_WINDOW_PARAMS
                        )
                        // in case of blocked popup, display the link
                        if (!popup || popup.closed) {
                            setOAuth2Link(link)
                        }
                    } else {
                        // if the EMP is in a popup, redirect to the oauth link instead of opening another popup
                        window.location.replace(link)
                    }
                } else {
                    // No link returned (e.g., connector requires form input)
                    // Stop showing the auto-connect spinner so the form can render
                    setIsAutoConnecting(false)
                }
            })
            .catch((err) => {
                console.debug(err)
                setModalError(getConnectorErrorMessage(err))
                setIsAutoConnecting(false)
                postMessage('pandium-error')
            })
    }

    // TODO this is custom for kombo..
    const [komboToken, setKomboToken] = useState('')

    useEffect(() => {
        if (!isEmpty(komboToken)) {
            connectBasic(
                connector.name,
                get(tenant, 'integration.name'),
                get(tenant, 'id'),
                { token: komboToken }
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [komboToken])

    useEffect(() => {
        setConnected(isConnected(tenant, extraTenantInfo.connector_name))
    }, [tenant, extraTenantInfo.connector_name])

    // on successful connect, post a message to the window that the emp is embedded in
    useEffect(() => {
        if (connected) {
            postMessage('pandium-success')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected])

    // set the tab/popup title
    useEffect(() => {
        document.title = authDialogTitle
    }, [authDialogTitle])

    useEffect(() => {
        // Datev's two-step OAuth flow doesn't need the pre-OAuth form even though
        // its YAML has `multi_step: true` — it auto-connects round 1 directly.
        const isDatev = isDatevConnector(connector?.name)
        const requiresPreOauthForm =
            get(connector, 'metadata.multi_step') === true && !isDatev

        // If we're already past round 1, render the form (with the client-selection
        // dropdown) instead of firing oauth_step:1 again and restarting the flow.
        const isDatevPendingSecondOauth =
            isDatev && authStatus === 'Pending second OAuth'

        const shouldAutoConnect =
            !isConnected(tenant, extraTenantInfo.connector_name) &&
            !isDatevPendingSecondOauth &&
            connector?.name !== 'kombo' &&
            connector.type !== 'basic' &&
            !requiresPreOauthForm &&
            ['web', 'hybrid', 'custom', 'oidc_hybrid'].includes(
                get(connector, 'metadata.connection_flow')
            )

        if (shouldAutoConnect) {
            setIsAutoConnecting(true)
            connectRequest(
                connector.name,
                undefined,
                isDatev ? { oauth_step: 1 } : {}
            )
        } else {
            setIsAutoConnecting(false)
        }
        // eslint-disable-next-line
    }, [connected, authStatus])

    // Show loading while determining if we should auto-connect
    if (isAutoConnecting === null || isAutoConnecting === true) {
        return (
            <CircularProgress
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                }}
            />
        )
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: { xs: '16px', sm: '20px' },
                boxSizing: 'border-box',
                overflow: 'auto',
                backgroundColor: 'background.default',
            }}
        >
            <Paper
                elevation={2}
                sx={{
                    width: '100%',
                    maxWidth: '600px',
                    minWidth: { xs: '320px', sm: '400px' },
                    padding: { xs: '16px', sm: '24px' },
                    margin: 'auto',
                }}
                role={'form'}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                    }}
                >
                    <Box
                        component="img"
                        sx={{
                            width: { xs: '32px', sm: '40px' },
                            height: 'auto',
                            flexShrink: 0,
                        }}
                        src={connector.logoUrl}
                        alt="Connector logo"
                    />
                    <DialogTitle
                        sx={{
                            padding: 0,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        }}
                    >
                        {authDialogTitle}
                    </DialogTitle>
                </Box>
                {!connected ? (
                    <ConnectDialog
                        close={() => null}
                        open={true}
                        connectorName={connector.name}
                        connector={connector}
                        tenant={tenant}
                        oAuth2Link={oAuth2Link}
                        modalError={modalError}
                        modalLoading={false}
                        connectRequest={connectRequest}
                        connectBasic={connectBasic}
                        mode="external"
                        datevClients={datevClients}
                        datevClientErrors={datevClientErrors}
                        authStatus={authStatus}
                        onStartOver={disconnectRequest}
                    />
                ) : (
                    <>
                        <DialogContent
                            sx={{ overflow: 'hidden' }}
                            role="formContent"
                        >
                            <ContentBlock
                                record={{ text: successMessage }}
                                source="text"
                                sx={{
                                    textAlign: 'center',
                                    marginTop: '25px',
                                }}
                            />
                        </DialogContent>
                    </>
                )}
            </Paper>
        </Box>
    )
}

const isConnected = (tenant, connectorName) => {
    const connectionStatus =
        tenant.status.auth?.[connectorName.toUpperCase() + '_AUTH_STATUS']
    if (connectionStatus === 'Connected') {
        return true
    }
    return false
}

const ExternalConnectorFormWrapper = (props) => {
    const dataProvider = useDataProvider()
    const [connector, setConnector] = useState()
    const [tenant, setTenant] = useState()
    const [webhookUrl, setWebhookUrl] = useState()
    const [loadError, setLoadError] = useState(null)

    // tenantId will be in url params if this page is being
    // accessed via /connector/tenants/:tenantId/edit/connection-form
    const { tenantId } = useParams()

    const runAuthDialogQuery =
        window.location.pathname.split('/')[
            window.location.pathname.split('/').length - 1
        ] !== 'connection-form'
    const {
        data: connectorData,
        loading: connectorLoading,
        error: connectorError,
    } = useGetMany(
        'connectors',
        {
            ids: [props.extraTenantInfo?.connector_name],
        },
        { enabled: !!props.extraTenantInfo?.connector_name }
    )

    useEffect(() => {
        if (!isEmpty(connectorData)) {
            setConnector(connectorData[0])
        }
    }, [connectorData])

    const tenantCallback = (event) => {
        if (runAuthDialogQuery) {
            setTenant(event.payload.tenant)
            setWebhookUrl(event.payload.webhookUrl)
        } else {
            setTenant(event.payload)
        }
    }

    useEffect(() => {
        let subscribeUrl
        if (runAuthDialogQuery) {
            let integrationName = props?.extraTenantInfo?.integration_name
            let token = props?.extraTenantInfo?.token
            const tid = props?.tid
            subscribeUrl = token
                ? `authdialog/${integrationName}?token=${token}${
                      tid ? `?tid=${tid}` : ''
                  }`
                : `authdialog/${integrationName}${tid ? `?tid=${tid}` : ''}`
        } else {
            subscribeUrl = `tenants/${tenantId}`
        }
        dataProvider.subscribe(subscribeUrl, tenantCallback).catch((error) => {
            setLoadError(error.body?.detail ?? 'An unknown error occurred')
        })
        return () => dataProvider.unsubscribe(subscribeUrl, tenantCallback)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (isEmpty(connector) && !connectorLoading) {
        console.debug('ExternalConnectorForm: connector not found', {
            connectorName: props.extraTenantInfo?.connector_name,
            extraTenantInfo: props.extraTenantInfo,
        })
    }
    if (isEmpty(tenant)) {
        console.debug('ExternalConnectorForm: tenant not loaded', {
            integrationName: props.extraTenantInfo?.integration_name,
            extraTenantInfo: props.extraTenantInfo,
        })
    }

    if (connectorLoading) {
        return (
            <CircularProgress
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                }}
            />
        )
    }

    if (connectorError || loadError) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    inset: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: { xs: '16px', sm: '20px' },
                    boxSizing: 'border-box',
                    backgroundColor: 'background.default',
                }}
            >
                <Paper
                    elevation={2}
                    sx={{
                        width: '100%',
                        maxWidth: '500px',
                        padding: { xs: '16px', sm: '24px' },
                        margin: 'auto',
                        textAlign: 'center',
                    }}
                >
                    <DialogTitle sx={{ color: 'error.main' }}>
                        Connection error
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {loadError || 'Error loading connector data'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Please contact your administrator for assistance.
                        </Typography>
                    </DialogContent>
                </Paper>
            </Box>
        )
    }

    if (!isEmpty(tenant) && !isEmpty(connector)) {
        return (
            <ExternalConnectorForm
                connector={connector}
                tenant={tenant}
                webhookUrl={webhookUrl}
                extraTenantInfo={props.extraTenantInfo}
            />
        )
    }

    return (
        <CircularProgress
            style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
            }}
        />
    )
}

const mapStateToProps = (state) => {
    return {
        extraTenantInfo: state.user.xti,
        tid: state.user.tid,
    }
}

export default connect(mapStateToProps)(ExternalConnectorFormWrapper)
